import React, { memo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Text, Icon, Input, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

const TitleInput = memo(({ title, onChange, onSave }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title || '');

    // Sync editValue when title prop changes externally
    useEffect(() => {
        if (!isEditing) {
            setEditValue(title || '');
        }
    }, [title, isEditing]);

    const handleActivateEdit = useCallback(() => {
        setIsEditing(true);
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setEditValue(title || '');
    }, [title]);

    const handleSave = useCallback(() => {
        if (onSave) {
            onSave(editValue);
        }
        setIsEditing(false);
    }, [editValue, onSave]);

    const handleInputChange = useCallback(
        (value) => {
            setEditValue(value);
            if (onChange) {
                onChange(value);
            }
        },
        [onChange],
    );

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                handleSave();
            } else if (e.key === 'Escape') {
                handleCancel();
            }
        },
        [handleSave, handleCancel],
    );

    const hasTitle = title && title.trim().length > 0;
    const displayText = hasTitle
        ? title
        : getLiteral('placeholder_marketing_campaigns_add_campaign_name');

    const textColor = hasTitle ? theme.colors.textLight.primary : theme.colors.textLight.disabled;

    if (isEditing) {
        return (
            <div className="campaign-details__title-input-editing">
                <div className="campaign-details__title-input-field">
                    <Input
                        value={editValue}
                        size="small"
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        placeholder={getLiteral(
                            'placeholder_marketing_campaigns_add_campaign_name',
                        )}
                        hideClear
                    />
                </div>
                <div className="campaign-details__title-input-actions">
                    <Icon
                        name="check"
                        className="campaign-details__title-input-save"
                        onClick={handleSave}
                        color={theme.colors.actionMajor[500]}
                    />
                    <Icon
                        name="close"
                        className="campaign-details__title-input-cancel"
                        onClick={handleCancel}
                        color={theme.colors.semantic.negative500}
                    />
                </div>
            </div>
        );
    }

    return (
        <Tooltip content={getLiteral('label_edit_name')}>
            <div
                className="campaign-details__title-input"
                onClick={handleActivateEdit}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleActivateEdit()}
            >
                <Text
                    type="h5"
                    className="campaign-details__title-input-text"
                    style={{ color: textColor }}
                >
                    {displayText}
                </Text>
                <Icon
                    name="edit"
                    className="campaign-details__title-input-edit-icon"
                    color={theme.colors.actionMajor[500]}
                />
            </div>
        </Tooltip>
    );
});

TitleInput.displayName = 'TitleInput';

TitleInput.propTypes = {
    title: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    onSave: PropTypes.func,
};

export default TitleInput;
